import { FirmwareModel } from '@core/models';

/** Which backend serves a device's firmware update. */
export const FIRMWARE_TRANSPORT = {
  Dlms: 'dlms',
  EnnyLcu: 'enny_lcu',
} as const;

export type FirmwareTransport = (typeof FIRMWARE_TRANSPORT)[keyof typeof FIRMWARE_TRANSPORT];

/** Device-func caps a bulk DLMS start at 25 devices; the EnnyLCU backend accepts far more. */
export const DLMS_BULK_UPDATE_LIMIT = 25;
export const ENNY_LCU_BULK_UPDATE_LIMIT = 100;

export type FirmwareStatus = 'Up to date' | 'Update available' | 'Unknown version' | 'Unknown';

export const FIRMWARE_STATUSES: readonly FirmwareStatus[] = [
  'Up to date',
  'Update available',
  'Unknown version',
  'Unknown',
];

export const FIRMWARE_STATUS_I18N_KEYS: Record<FirmwareStatus, string> = {
  'Up to date': 'firmware.status.upToDate',
  'Update available': 'firmware.status.updateAvailable',
  'Unknown version': 'firmware.status.unknownVersion',
  Unknown: 'firmware.status.unknown',
};

export interface FirmwareCatalogueState {
  status: FirmwareStatus;
  /** Top-ranked active row for the device type, if the catalogue has one. */
  latest: FirmwareModel | undefined;
  /** Rows the user may pick in the update dialog. Empty means "offer nothing". */
  offered: FirmwareModel[];
  /** The installed version is not a catalogue row, so the offered rows are not an upgrade path
   * and the UI must label them as such. */
  installedNotInCatalogue: boolean;
}

export interface DeviceTypeProtocol {
  deviceTypeId: number;
  protocol?: string | null;
}

/** The device-type description is not usable as a key: CompactIQ reads 'CompactIQ' in dev and
 * 'IQ0404100K1+R | COMPACTIQ RS485 ENNY' in prd. The transport is therefore resolved from the
 * numeric deviceTypeId against the device-type catalogue, which is the only place the protocol is
 * reliably available: `GET /devices` serializes through a model that drops `protocol`, so a device
 * coming from the list has none, while `GET /devices/{id}` does carry it. The device's own
 * protocol is only a fallback for when the device-type catalogue has not loaded yet. */
export function resolveFirmwareTransport(
  device: { protocol?: string | null; deviceTypeId?: number } | undefined,
  deviceTypes: readonly DeviceTypeProtocol[] = [],
): FirmwareTransport {
  const fromCatalogue = deviceTypes.find(dt => dt.deviceTypeId === device?.deviceTypeId)?.protocol;
  const protocol = (fromCatalogue ?? device?.protocol ?? '').trim().toUpperCase();
  return protocol === 'DLMS' ? FIRMWARE_TRANSPORT.Dlms : FIRMWARE_TRANSPORT.EnnyLcu;
}

export function bulkUpdateLimitFor(transport: FirmwareTransport): number {
  return transport === FIRMWARE_TRANSPORT.Dlms
    ? DLMS_BULK_UPDATE_LIMIT
    : ENNY_LCU_BULK_UPDATE_LIMIT;
}

function isMissingVersion(version: string | null | undefined): boolean {
  const trimmed = (version ?? '').trim();
  return !trimmed || trimmed === 'null' || trimmed === 'undefined';
}

function releaseTime(firmware: FirmwareModel): number | undefined {
  if (!firmware.releaseDate) return undefined;
  const parsed = Date.parse(firmware.releaseDate);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/** Active rows for one device type, newest first. Version strings are never compared: they range
 * from 'v1.2.0-rc1' to '11100016' to 'L710TK_V10.09.01.16_00000101', so ordering comes from the
 * catalogue itself — releaseDate descending, firmwareId descending where releaseDate is absent
 * (rows without a release date rank below every dated row). */
export function rankFirmwareCatalogue(
  catalogue: readonly FirmwareModel[],
  deviceTypeId: number | undefined,
): FirmwareModel[] {
  return catalogue
    .filter(fw => fw.isActive && fw.deviceTypeId === deviceTypeId)
    .slice()
    .sort((a, b) => {
      const aTime = releaseTime(a);
      const bTime = releaseTime(b);
      if (aTime !== undefined && bTime !== undefined && aTime !== bTime) return bTime - aTime;
      if (aTime !== undefined && bTime === undefined) return -1;
      if (aTime === undefined && bTime !== undefined) return 1;
      return b.firmwareId - a.firmwareId;
    });
}

/** Resolves the four firmware states from the catalogue ranking. A device running something the
 * catalogue does not know about (including something newer) reads 'Unknown version' rather than
 * 'Up to date', which also stops the UI silently offering a downgrade. */
export function resolveFirmwareCatalogueState(
  catalogue: readonly FirmwareModel[],
  deviceTypeId: number | undefined,
  installedVersion: string | null | undefined,
): FirmwareCatalogueState {
  const ranked = rankFirmwareCatalogue(catalogue, deviceTypeId);
  const latest = ranked[0];

  if (isMissingVersion(installedVersion)) {
    return { status: 'Unknown', latest, offered: ranked, installedNotInCatalogue: false };
  }

  const installed = (installedVersion ?? '').trim();
  const installedIndex = ranked.findIndex(fw => fw.version === installed);

  if (installedIndex < 0) {
    return { status: 'Unknown version', latest, offered: ranked, installedNotInCatalogue: true };
  }
  if (installedIndex === 0) {
    return { status: 'Up to date', latest, offered: [], installedNotInCatalogue: false };
  }
  return {
    status: 'Update available',
    latest,
    offered: ranked.slice(0, installedIndex),
    installedNotInCatalogue: false,
  };
}
